import {useMemo, useCallback, useState} from 'react'
import {Box, Notification} from '@binance/uikit'
import useOrderForm from 'hooks/useOrderForm'
import useRouter from 'hooks/useRouter'
import useUserLoginStatus from 'hooks/useUserLoginStatus'
import useSymbolInfo from 'hooks/useSymbolInfo'
import {useDispatch} from 'redux-react-hook'
import {
  updateOrderQty, resetOrderForm, updateOrderTradeStatus, updateReduceOnly
} from 'actions'
import {TRADE_TYPES, TRADE_SIDES} from 'constants'
import {floor} from 'utils/number'
import RequireLoginEl from './RequireLoginEl'
import MarketForm from './MarketForm'
import ConfirmDialog from './ConfirmDialog'
import TradeLimitType from './TradeLimitType'

export default function MarketFormContainer ({i18n}) {
  const [side, setSide] = useState(TRADE_SIDES.BUY)
  const [showDialog, setShowDialog] = useState(false)
  const dispatch = useDispatch()
  const formType = TRADE_TYPES.MARKET

  const isLogin = useUserLoginStatus()
  const router = useRouter()
  const {symbol} = router.query
  const {symbolInfo} = useSymbolInfo()

  const {
    baseAsset, quoteAsset, quantityPrecision, maxLen
  } = symbolInfo

  const {
    handleBuy,
    handleSell,
    availableBalance,
    marginBuyRequired,
    marginSellRequired,
    marketMaxQty,
    buyQty,
    sellQty,
    buyStatus,
    sellStatus,
    maxBuyQty,
    maxSellQty,
    reduceOnly,
    positionSize
  } = useOrderForm(symbol, quoteAsset, formType)

  const reset = useCallback(
    buyOrSell => {
      dispatch(resetOrderForm(buyOrSell, formType))
    },
    [dispatch, formType]
  )

  const setLimitType = useCallback(
    () => {
      dispatch(updateReduceOnly(formType, !reduceOnly))
    },
    [dispatch, formType, reduceOnly]
  )

  const switchTradeStatus = useCallback(
    (buyOrSell, status) => {
      dispatch(updateOrderTradeStatus(buyOrSell, status, formType))
    },
    [dispatch, formType]
  )

  const switchDialog = useCallback(() => {
    setShowDialog(!showDialog)
  }, [showDialog])

  const buyHandler = useCallback(
    isConfirmed => {
      const bQty = Number(buyQty)
      if (bQty > 0) {
        const tradeSide = TRADE_SIDES.BUY
        setSide(tradeSide)
        const isTrade = isConfirmed === true
          || JSON.parse(window.localStorage.getItem(`dialog_${formType}_${tradeSide}`))
        if (isTrade) {
          reset(tradeSide)
          switchTradeStatus(tradeSide, true)
          handleBuy().then(d => {
            switchTradeStatus(tradeSide, false)
            if (d?.order) {
              Notification({
                content: i18n['marketForm-buySuccess'],
                type: 'success',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            } else {
              Notification({
                content: d?.error?.message || i18n['marketForm-buyFailure'],
                type: 'error',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            }
          })
        } else {
          switchDialog()
        }
      }
    },
    [buyQty, formType, handleBuy, i18n, reset, switchDialog, switchTradeStatus]
  )

  const sellHandler = useCallback(
    isConfirmed => {
      const sQty = Number(sellQty)
      if (sQty > 0) {
        const tradeSide = TRADE_SIDES.SELL
        setSide(tradeSide)
        const isTrade = isConfirmed === true
          || JSON.parse(window.localStorage.getItem(`dialog_${formType}_${tradeSide}`))
        if (isTrade) {
          reset(tradeSide)
          switchTradeStatus(tradeSide, true)
          handleSell().then(d => {
            switchTradeStatus(tradeSide, false)
            if (d?.order) {
              Notification({
                content: i18n['marketForm-sellSuccess'],
                type: 'success',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            } else {
              Notification({
                content: d?.error?.message || i18n['marketForm-sellFailure'],
                type: 'error',
                duration: 3000
                // time: formatDate(new Date(), 'HH:mm:ss')
              })
            }
          })
        } else {
          switchDialog()
        }
      }
    },
    [formType, handleSell, i18n, reset, sellQty, switchDialog, switchTradeStatus]
  )

  const getBuySliderAvailableRange = useCallback(() => {
    let availableRange = {min: 0, max: 100}
    const pSize = Number(positionSize)
    if (isLogin) {
      if (reduceOnly) {
        if (pSize) {
          pSize > 0 && (availableRange = {min: 0, max: 0})
        } else {
          availableRange = {min: 0, max: 0}
        }
      }
      if (maxBuyQty === 0) {
        availableRange = {min: 0, max: 0}
      }
    } else {
      availableRange = {min: 0, max: 0}
    }
    return availableRange
  }, [isLogin, positionSize, reduceOnly, maxBuyQty])

  const getSellSliderAvailableRange = useCallback(() => {
    let availableRange = {min: 0, max: 100}
    const pSize = Number(positionSize)
    if (isLogin) {
      if (reduceOnly) {
        if (pSize) {
          pSize < 0 && (availableRange = {min: 0, max: 0})
        } else {
          availableRange = {min: 0, max: 0}
        }
        if (maxSellQty === 0) {
          availableRange = {min: 0, max: 0}
        }
      }
    } else {
      availableRange = {min: 0, max: 0}
    }
    return availableRange
  }, [isLogin, positionSize, reduceOnly, maxSellQty])

  const changeSliderValue = useCallback(({val, type}) => {
    const maxQty = type === 'buy' ? maxBuyQty : maxSellQty
    val = floor(maxQty * val / 100, quantityPrecision)
    dispatch(updateOrderQty(type, val, formType))
  }, [maxBuyQty, maxSellQty, quantityPrecision, dispatch, formType])

  return useMemo(
    () => (
      <>
        {showDialog && (
          <ConfirmDialog
            i18n={i18n}
            visible={showDialog}
            type={formType}
            side={side}
            onClose={switchDialog}
            handleBuy={buyHandler}
            handleSell={sellHandler}
          />
        )}
        <Box mx={5} my={3}>
          <TradeLimitType i18n={i18n} isLimit={false} availableBalance={availableBalance}
            setLimitType={setLimitType} reduceOnly={reduceOnly} enableReduceOnly={true}
          />
        </Box>
        <MarketForm
          i18n={i18n}
          isLogin={isLogin}
          requireLoginEl={<RequireLoginEl i18n={i18n} />}
          baseAsset={baseAsset}
          quoteAsset={quoteAsset}
          quantityPrecision={quantityPrecision}
          maxLen={maxLen}
          marketMaxQty={marketMaxQty}
          buyQty={buyQty}
          sellQty={sellQty}
          marginBuyRequired={marginBuyRequired}
          marginSellRequired={marginSellRequired}
          handleQtyChange={(buyOrSell, qty) => {
            dispatch(updateOrderQty(buyOrSell, qty, formType))
          }}
          handleBuy={buyHandler}
          handleSell={sellHandler}
          buyStatus={buyStatus}
          sellStatus={sellStatus}
          reduceOnly={reduceOnly}
          buyAvailableRange={getBuySliderAvailableRange()}
          sellAvailableRange={getSellSliderAvailableRange()}
          changeSliderValue={changeSliderValue}
        />
      </>
    ),
    [showDialog, i18n, formType, side, switchDialog, buyHandler, sellHandler, availableBalance, setLimitType, reduceOnly, isLogin, baseAsset, quoteAsset, quantityPrecision, maxLen, marketMaxQty, buyQty, sellQty, marginBuyRequired, marginSellRequired, buyStatus, sellStatus, getBuySliderAvailableRange, getSellSliderAvailableRange, changeSliderValue, dispatch]
  )
}
